import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { trainingGroupsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { CountryCodeSelect } from '../components/ui/CountryCodeSelect';

const Register = () => {
  const { user, register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    country_code: '+31', // Default to Netherlands
    language: 'en', // Default to English
    role: 'rower',
    preferred_side: 'none',
    requested_training_group_id: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);

  // Get training groups for selection (public endpoint, no auth required)
  const { data: trainingGroups = [], isLoading: isLoadingGroups } = useQuery(
    'publicTrainingGroups',
    () => trainingGroupsAPI.getPublicGroups(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (error) {
      setError('');
    }

    // Auto-strip leading zeros from phone number and validate
    if (name === 'phone_number') {
      const cleanedValue = value.replace(/^0+/, '').replace(/\D/g, ''); // Remove leading zeros and non-digits
      setFormData({
        ...formData,
        [name]: cleanedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return false; // Phone is required in registration
    const cleanPhone = phone.replace(/\D/g, ''); // Remove non-digits
    return cleanPhone.length >= 5;
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 6) {
      errors.push('at least 6 characters');
    }

    if (!/[A-Za-z]/.test(password)) {
      errors.push('at least one letter');
    }

    if (!/\d/.test(password)) {
      errors.push('at least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('at least one special character');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password requirements
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(`Password must contain ${passwordErrors.join(', ')}`);
      setLoading(false);
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phone_number)) {
      setError('Phone number must be at least 5 digits long');
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;

      // Convert empty training group selection to null
      if (registerData.requested_training_group_id === '') {
        registerData.requested_training_group_id = null;
      } else {
        registerData.requested_training_group_id = parseInt(
          registerData.requested_training_group_id
        );
      }

      const result = await register(registerData);

      if (result.success) {
        setRegistrationResult(result);
        setSuccess(true);
      } else {
        setError(result.error);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const isAutoAdmin = registrationResult?.is_admin;
    const requiresApproval = registrationResult?.requires_approval;

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-green-600">
              <span className="text-lg font-bold text-white">✓</span>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {isAutoAdmin ? 'Welcome, Administrator!' : 'Registration Successful!'}
            </h2>
            <div className="mt-2 text-center">
              {isAutoAdmin ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Your admin account has been activated immediately.
                  </p>
                  <Link to="/login" className="font-medium text-rowing-600 hover:text-rowing-500">
                    Sign in to access the system
                  </Link>
                </div>
              ) : requiresApproval ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Your account has been created and is pending approval by an administrator. You
                    will receive an email confirmation once your account is approved.
                  </p>
                  <p className="text-xs text-gray-500">
                    Already have an account?{' '}
                    <Link to="/login" className="font-medium text-rowing-600 hover:text-rowing-500">
                      Sign in here
                    </Link>
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Your account has been created successfully.
                  </p>
                  <Link to="/login" className="font-medium text-rowing-600 hover:text-rowing-500">
                    Sign in here
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-rowing-600">
            <span className="text-lg font-bold text-white">RP</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-rowing-600 hover:text-rowing-500">
              Sign in here
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4" role="alert">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="form-label">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="last_name" className="form-label">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
              {formData.email.endsWith('@usrtriton.nl') && (
                <p className="mt-1 text-xs text-blue-600">
                  ✓ This email will grant automatic admin access
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone_number" className="form-label">
                Phone Number
              </label>
              <div className="flex">
                <CountryCodeSelect
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                />
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  required
                  className="form-input flex-1 rounded-l-none"
                  placeholder="612345678"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Phone number must be at least 5 digits. Leading zeros will be automatically removed.
              </p>
              {formData.phone_number && !validatePhoneNumber(formData.phone_number) && (
                <p className="mt-1 text-xs text-red-600">
                  Phone number must be at least 5 digits long
                </p>
              )}
            </div>

            <fieldset className="m-0 min-w-0 space-y-2 border-0 p-0">
              <legend className="form-label mb-0 px-0">Role</legend>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="role_rower"
                    name="role"
                    type="radio"
                    value="rower"
                    checked={formData.role === 'rower'}
                    onChange={handleChange}
                    className="h-4 w-4 border-gray-300 text-rowing-600 focus:ring-rowing-500"
                  />
                  <label htmlFor="role_rower" className="ml-3 text-sm font-medium text-gray-700">
                    Rower
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="role_coach"
                    name="role"
                    type="radio"
                    value="coach"
                    checked={formData.role === 'coach'}
                    onChange={handleChange}
                    className="h-4 w-4 border-gray-300 text-rowing-600 focus:ring-rowing-500"
                  />
                  <label htmlFor="role_coach" className="ml-3 text-sm font-medium text-gray-700">
                    Coach
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Admin role can only be granted by existing administrators
                </p>
              </div>
            </fieldset>

            <div>
              <label htmlFor="requested_training_group_id" className="form-label">
                Training Group (Optional)
              </label>
              {isLoadingGroups ? (
                <div className="form-input flex items-center">
                  <LoadingSpinner size="small" />
                  <span className="ml-2 text-gray-500">Loading training groups...</span>
                </div>
              ) : (
                <select
                  id="requested_training_group_id"
                  name="requested_training_group_id"
                  className="form-input"
                  value={formData.requested_training_group_id}
                  onChange={handleChange}
                >
                  <option value="">Select a training group (optional)</option>
                  {trainingGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Choose the training group you'd like to join. This will be reviewed by
                administrators.
              </p>
            </div>

            <div>
              <label htmlFor="preferred_side" className="form-label">
                Preferred Side
              </label>
              <select
                id="preferred_side"
                name="preferred_side"
                className="form-input"
                value={formData.preferred_side}
                onChange={handleChange}
              >
                <option value="none">No Preference</option>
                <option value="port">Port (Left)</option>
                <option value="starboard">Starboard (Right)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select your preferred rowing side, if any
              </p>
            </div>

            <div>
              <label htmlFor="language" className="form-label">
                Language
              </label>
              <select
                id="language"
                name="language"
                className="form-input"
                value={formData.language}
                onChange={handleChange}
              >
                <option value="en">🇬🇧 English</option>
                <option value="nl">🇳🇱 Nederlands</option>
                <option value="de">🇩🇪 Deutsch</option>
                <option value="fr">🇫🇷 Français</option>
                <option value="es">🇪🇸 Español</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select your preferred language for the application
              </p>
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="form-input"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
              <div className="mt-1 text-xs text-gray-500">
                <p>Password must contain:</p>
                <ul className="mt-1 list-inside list-disc space-y-1">
                  <li className={formData.password.length >= 6 ? 'text-green-600' : ''}>
                    At least 6 characters
                  </li>
                  <li className={/[A-Za-z]/.test(formData.password) ? 'text-green-600' : ''}>
                    At least one letter
                  </li>
                  <li className={/\d/.test(formData.password) ? 'text-green-600' : ''}>
                    At least one number
                  </li>
                  <li
                    className={
                      /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-green-600' : ''
                    }
                  >
                    At least one special character
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="form-input"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex w-full justify-center"
            >
              {loading ? <LoadingSpinner size="small" text="" /> : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
